import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InventoryBulkDelete = ({ selectedIds, onClear, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const count = selectedIds.length;

  if (count === 0) return null;

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/inventory/bulk-delete`, { ids: selectedIds });
      toast.success(`Deleted ${res.data.deleted_count} item${res.data.deleted_count === 1 ? "" : "s"}`);
      setOpen(false);
      onDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete items");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50/60 animate-in fade-in slide-in-from-top-1"
        data-testid="bulk-actions-bar"
      >
        <span className="text-sm font-medium text-red-800" data-testid="bulk-selected-count">
          {count} item{count === 1 ? "" : "s"} selected
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClear} data-testid="bulk-clear-btn">
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => setOpen(true)}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete Selected
          </Button>
        </div>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {count} item{count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected items from inventory. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading} data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bulk-delete-confirm"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete {count}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
